import { z } from 'zod';
import { actions, isActionId } from './actions.js';
/**
 * A routine is "when X (and Y), do these steps in order".
 * The same schema validates the editor in the apps and the file on the server.
 */
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Vreme mora biti u obliku HH:MM');
export const routineTrigger = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('voice'),
        /** Spoken phrase, matched without diacritics or punctuation, e.g. "spremi igru". */
        phrase: z.string().trim().min(2).max(80),
    }),
    z.object({
        type: z.literal('schedule'),
        time: hhmm,
        /** 0 = nedelja … 6 = subota; empty means every day. */
        days: z.array(z.number().int().min(0).max(6)).max(7).default([]),
    }),
    z.object({ type: z.literal('server_down') }),
    z.object({ type: z.literal('server_up') }),
    z.object({
        type: z.literal('player_join'),
        /** Names to watch for; empty means anyone. */
        names: z.array(z.string().trim().min(1).max(60)).max(30).default([]),
    }),
]);
export const routineCondition = z.discriminatedUnion('type', [
    z.object({ type: z.literal('server_online') }),
    z.object({ type: z.literal('pc_connected') }),
    z.object({ type: z.literal('time_between'), from: hhmm, to: hhmm }),
]);
export const routineStep = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('action'),
        action: z.string().refine(isActionId, 'Nepoznata akcija'),
        params: z.record(z.string(), z.unknown()).default({}),
    }),
    z.object({ type: z.literal('wait'), seconds: z.number().int().min(1).max(600) }),
    z.object({ type: z.literal('say'), text: z.string().trim().min(1).max(300) }),
]);
export const routineInput = z
    .object({
    name: z.string().trim().min(1).max(60),
    enabled: z.boolean().default(true),
    triggers: z.array(routineTrigger).max(10).default([]),
    conditions: z.array(routineCondition).max(10).default([]),
    steps: z.array(routineStep).min(1, 'Rutina mora imati bar jedan korak').max(20),
    /** Stop at the first failed step instead of continuing. */
    stopOnError: z.boolean().default(false),
})
    .superRefine((r, ctx) => {
    r.steps.forEach((step, i) => {
        if (step.type !== 'action' || !isActionId(step.action))
            return;
        const parsed = actions[step.action].params.safeParse(step.params);
        if (!parsed.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['steps', i, 'params'],
                message: parsed.error.issues.map((x) => x.message).join('; '),
            });
        }
    });
});
const cyrillic = {
    а: 'a',
    б: 'b',
    в: 'v',
    г: 'g',
    д: 'd',
    ђ: 'dj',
    е: 'e',
    ж: 'z',
    з: 'z',
    и: 'i',
    ј: 'j',
    к: 'k',
    л: 'l',
    љ: 'lj',
    м: 'm',
    н: 'n',
    њ: 'nj',
    о: 'o',
    п: 'p',
    р: 'r',
    с: 's',
    т: 't',
    ћ: 'c',
    у: 'u',
    ф: 'f',
    х: 'h',
    ц: 'c',
    ч: 'c',
    џ: 'dz',
    ш: 's',
};
/**
 * "Spremi igru!", "spremi  igru" and "Спреми игру" all become "spremi igru".
 * Serbian recognizers often answer in Cyrillic, so both scripts compare equal.
 */
export function normalizePhrase(text) {
    return text
        .toLowerCase()
        .replace(/[а-шђјљњћџ]/g, (ch) => cyrillic[ch] ?? ch)
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/đ/g, 'dj')
        .replace(/[^\p{L}\p{N}\s]/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
/** True when the spoken text is the phrase, optionally with "Elena" or "hej" around it. */
export function matchesPhrase(spoken, phrase) {
    const s = normalizePhrase(spoken)
        .replace(/^(hej |ej )?([jh]?elena )?/, '')
        .replace(/ [jh]?elena$/, '');
    return s === normalizePhrase(phrase);
}
export const dayLabels = ['ned', 'pon', 'uto', 'sre', 'čet', 'pet', 'sub'];
export function describeTrigger(t) {
    switch (t.type) {
        case 'voice':
            return `Glas: „${t.phrase}“`;
        case 'schedule': {
            const days = t.days.length === 0 || t.days.length === 7
                ? 'svaki dan'
                : t.days.length === 5 && [1, 2, 3, 4, 5].every((d) => t.days.includes(d))
                    ? 'pon–pet'
                    : t.days.map((d) => dayLabels[d]).join(', ');
            return `${days} u ${t.time}`;
        }
        case 'server_down':
            return 'Kad server padne';
        case 'server_up':
            return 'Kad se server podigne';
        case 'player_join':
            return t.names.length ? `Kad uđe ${t.names.join(', ')}` : 'Kad neko uđe na server';
    }
}
export function describeCondition(c) {
    switch (c.type) {
        case 'server_online':
            return 'Server je online';
        case 'pc_connected':
            return 'Računar je povezan';
        case 'time_between':
            return `Između ${c.from} i ${c.to}`;
    }
}
/** Placeholders a routine can use in spoken text, notifications and messages. */
export const routineVariables = [
    { key: 'igrac', label: 'igrač koji je ušao' },
    { key: 'server', label: 'ime servera' },
    { key: 'vreme', label: 'trenutno vreme' },
];
/** Replaces {igrac}, {server}, {vreme}; unknown placeholders stay as written. */
export function fillVariables(text, vars) {
    return text.replace(/\{(\w+)\}/g, (all, key) => vars[key] ?? all);
}
const actionLabels = {
    'youtube.play': 'Pusti na YouTube-u',
    'media.control': 'Muzika',
    'fivem.launch': 'Upali FiveM',
    'fivem.connect': 'Uđi na server',
    'fivem.close': 'Ugasi FiveM',
    'elena.cleanup': 'Počisti duple Elene',
    'app.open': 'Otvori',
    'url.open': 'Otvori stranicu',
    'system.lock': 'Zaključaj računar',
    'system.shutdown': 'Ugasi računar',
    'routine.run': 'Pokreni rutinu',
    'server.status': 'Proveri server',
    'phone.notify': 'Notifikacija na telefon',
    'discord.send': 'Discord poruka',
    'spotify.play': 'Pusti na Spotify-u',
    'spotify.control': 'Spotify',
    'reminder.set': 'Podsetnik',
    'reminder.list': 'Pročitaj podsetnike',
    'reminder.cancel': 'Otkaži podsetnik',
    'server.stats': 'Statistika servera',
    'fivem.restart_resource': 'Restartuj resurs',
    'fivem.announce': 'Obaveštenje igračima',
    'fivem.kick': 'Izbaci igrača',
    'fivem.errors': 'Greške sa servera',
    'fivem.resources': 'Resursi servera',
    'fivem.restart_resources': 'Restartuj resurse',
    'fivem.restart_changed': 'Restartuj promenjene resurse',
    'fivem.restart_all': 'Restartuj sve resurse',
    'fivem.restart_server': 'Restartuj server',
    'fivem.restart_cancel': 'Otkaži restart servera',
    'fivem.recent_changes': 'Skorašnje izmene na serveru',
    'ops.status': 'Stanje OPS-a',
    'ops.branches': 'Git grane',
    'ops.server_plan': 'Plan deploya servera',
    'ops.server_deploy': 'Deploy servera',
    'ops.admin_deploy': 'Deploy admin panela',
    'ops.rollback': 'Rollback',
    'ops.job': 'OPS posao',
    'ops.service': 'Servis admin panela',
    'ops.jobs': 'OPS poslovi',
    'ops.logs': 'Logovi sa VPS-a',
    'ops.autodeploy': 'Auto deploy',
    'elena.mood': 'Raspoloženje',
    'elena.language': 'Jezik',
    'weather.get': 'Vremenska prognoza',
    'tv.power': 'Televizor',
    'tv.app': 'Aplikacija na TV-u',
    'tv.youtube': 'YouTube na TV-u',
    'tv.control': 'TV kontrola',
    'tv.input': 'TV ulaz',
    'tv.notify': 'Poruka na TV-u',
    'ps5.power': 'PS5',
    'ps5.status': 'Stanje PS5',
    'calendar.list': 'Pročitaj kalendar',
    'calendar.add': 'Dodaj u kalendar',
    'calendar.delete': 'Obriši iz kalendara',
    'notes.add': 'Zapiši belešku',
    'notes.read': 'Pročitaj beleške',
    'notes.delete': 'Obriši belešku',
    'memory.save': 'Zapamti',
    'memory.forget': 'Zaboravi',
    'music.find': 'Proveri pesmu',
    'speech.learn': 'Nauči izgovor',
    'speech.forget': 'Zaboravi izgovor',
};
/** One-line Serbian label for a step, e.g. "Pusti na YouTube-u: Ceca" or "Sačekaj 5 s". */
export function describeStep(step) {
    switch (step.type) {
        case 'wait':
            return `Sačekaj ${step.seconds} s`;
        case 'say':
            return `Kaži: „${step.text}“`;
        case 'action': {
            const label = actionLabels[step.action] ?? step.action;
            const detail = Object.values(step.params).find((v) => typeof v === 'string' || typeof v === 'number');
            return detail === undefined ? label : `${label}: ${detail}`;
        }
    }
}
//# sourceMappingURL=routines.js.map